import React from 'react';
import { Clock, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FleetNodeCard from './FleetNodeCard';

export const GptPlusFleetNotice = ({ notice }) => notice ? (
  <div role="note" className="mb-4 flex gap-2 rounded-lg border border-accent-100 bg-accent-50 px-3 py-2 text-sm text-accent-800">
    <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
    <p className="break-words">{notice}</p>
  </div>
) : null;

const GptPlusNodeCard = ({ node, onUse }) => {
  const { t, i18n } = useTranslation();
  const stopped = node.status !== 'online';
  const statusKey = stopped ? 'unavailable' : node.color === 'yellow' ? 'busy' : node.color === 'green' ? 'idle' : 'available';
  const statusLabel = statusKey === 'idle' ? 'idleRecommended' : statusKey === 'busy' ? 'busyAvailable' : statusKey;
  // 已合并进状态标签的文案不再重复占一行；其他后端说明照常展示。
  const statusWords = statusKey === 'idle' ? ['空闲', '推荐', '可用'] : statusKey === 'busy' ? ['繁忙', '可用'] : [];
  const description = (node.description || '').split(/[·|/]/).map(part => part.trim()).filter(part => part && !statusWords.includes(part)).join(' · ');
  const iqKey = node.isFullPower === true ? 'fullPower' : node.isFullPower === false ? 'degraded' : 'unknownPower';
  const formatDate = value => new Date(value).toLocaleString(i18n.language, {
    month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', year: 'numeric',
  });
  const featureKeys = { deep_research: 'deepResearch', file_upload: 'fileUpload', paste_text_to_file: 'pasteText', image_gen: 'imageGeneration' };
  const limits = node.limits || [];

  return (
    <FleetNodeCard node={node} onUse={onUse} stopped={stopped}
      statusText={t(`aiFleet.plusNodes.${statusLabel}`)}
      ariaLabel={t('aiFleet.plusNodes.nodeLabel', { id: node.id })}
      actionAriaLabel={t(stopped ? 'aiFleet.plusNodes.stoppedLabel' : 'aiFleet.plusNodes.useLabel', { id: node.id })}
      badges={node.isFullPower != null && <span className="fleet-card-quality">{t(`aiFleet.plusNodes.${iqKey}`)}</span>}>
      {description && <p className="mb-2 break-words text-xs leading-5 text-ink-500">{description}</p>}
      {limits.length > 0 && <div aria-label={t('aiFleet.plusNodes.quotaDetails')} className="fleet-card-limit-grid">
        {limits.map((limit, index) => {
          const feature = featureKeys[limit.feature] ? t(`aiFleet.plusNodes.features.${featureKeys[limit.feature]}`) : limit.feature;
          const remaining = limit.remaining !== null ? t('aiFleet.plusNodes.remaining', { count: limit.remaining }) : '';
          const timing = [
            limit.resetAt && t('aiFleet.plusNodes.resetAt', { time: formatDate(limit.resetAt) }),
            node.metadataUpdatedAt && t('aiFleet.plusNodes.updatedAt', { time: formatDate(node.metadataUpdatedAt) }),
          ].filter(Boolean).join('\n');
          return <span key={`${limit.feature}-${index}`} tabIndex={0} title={timing || undefined}
            aria-label={[feature, remaining, timing].filter(Boolean).join('，')}
            className={`fleet-card-limit ${limit.remaining === 0 ? 'is-empty' : ''}`}>
            <span className="min-w-0 break-words">{feature}</span>
            {limit.remaining !== null && <span className="font-semibold tabular-nums">{limit.remaining}</span>}
          </span>;
        })}
      </div>}
      {node.limitDescription && <p className="mb-3 break-words rounded-lg bg-amber-50 px-2 py-1.5 text-xs leading-5 text-amber-800">{node.limitDescription}</p>}
      {node.recoverySeconds > 0 && (
        <p className="mb-3 flex items-start gap-1 text-xs leading-5 text-ink-500">
          <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          {t('aiFleet.plusNodes.recovery', { minutes: Math.ceil(node.recoverySeconds / 60) })}
        </p>
      )}
    </FleetNodeCard>
  );
};

export default GptPlusNodeCard;
